/**
 * PATH: packages/job-hub/src/jobs/qa/qaApiHelpers.ts
 * FILE_ID: JOBHUB-QA-API-HELPERS-001
 * PURPOSE: Pure normalization, labeling, provenance, and progress helpers.
 */
import type {
  QaAreaExecution,
  QaChecklistInstance,
  QaEvidence,
  QaItemInstance,
  QaGenerationProvenance,
  QaInspectionSummary,
  QaJobFile,
  QaPreviewPlan,
  QuoteSheetOption,
} from './qaTypes'

export function normalizeList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown }).results)) {
    return (data as { results: T[] }).results
  }
  return []
}

/** Quotes/files picker shape: list is `results`, search is `files`. */
export function qdrivePickerFilesFromResponse(data: unknown): QaJobFile[] {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : Array.isArray((data as { files?: unknown[] })?.files)
        ? (data as { files: unknown[] }).files
        : []
  return raw.map(normalizeQaJobFile).filter((file): file is QaJobFile => file !== null)
}

function normalizeQaJobFile(row: unknown): QaJobFile | null {
  if (!row || typeof row !== 'object') return null
  const data = row as Record<string, unknown>
  const source = (data.file && typeof data.file === 'object' ? data.file : data) as Record<string, unknown>
  const id = source.id ?? source.file_id ?? data.id
  const filename = source.filename ?? source.original_filename ?? source.name ?? data.filename ?? data.name
  if (id == null || !filename) return null
  return {
    id: String(id),
    filename: String(filename),
    content_type: (source.content_type ?? source.mime_type ?? data.content_type) as string | undefined,
    file_size: typeof source.file_size === 'number' ? source.file_size : undefined,
    created_at: (source.uploaded_at ?? source.created_at ?? data.created_at) as string | undefined,
  }
}

export function toQuoteSheetOptions(data: unknown): QuoteSheetOption[] {
  return normalizeList<Record<string, unknown>>(data)
    .map((row) => ({
      id: Number(row.id),
      quote_number: (row.quote_number as string) ?? null,
      quote_name: (row.quote_name as string) ?? null,
      sequence_number: (row.sequence_number as number) ?? 0,
      status: (row.status as string) ?? undefined,
    }))
    .filter((option) => Number.isFinite(option.id))
}

export function quoteSheetLabel(option: QuoteSheetOption): string {
  const name = (option.quote_name || option.quote_number || `Quote ${option.id}`).trim()
  return option.sequence_number === 0 ? `${name} (Contract)` : name
}

export function qaScopeName(base: string | undefined, scopeNumber: number): string {
  const trimmed = (base ?? '').trim()
  return trimmed ? `${trimmed} ${scopeNumber}` : `QA Scope ${scopeNumber}`
}

/** Smallest unused default names. Without a job number, only an exact `QA <number>` occupies N. With one, slot 1 is `{job}_QA` and slot n is `{job}_QA_n`. */
export function nextDefaultQaNames(
  existingNames: readonly string[],
  count: number,
  jobNumber?: string | number,
): string[] {
  const job = jobNumber == null ? '' : String(jobNumber).trim()
  if (!job) {
    const used = new Set<number>()
    for (const name of existingNames) {
      const match = /^QA (\d+)$/.exec(name.trim())
      if (match) used.add(Number(match[1]))
    }
    const names: string[] = []
    let n = 1
    while (names.length < count) {
      if (!used.has(n)) names.push(`QA ${n}`)
      n += 1
    }
    return names
  }
  const slot1 = `${job}_QA`
  const numbered = new RegExp(`^${job.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_QA_(\\d+)$`)
  const used = new Set<number>()
  for (const name of existingNames) {
    const trimmed = name.trim()
    if (trimmed === slot1) used.add(1)
    const match = numbered.exec(trimmed)
    if (match) used.add(Number(match[1]))
  }
  const names: string[] = []
  let n = 1
  while (names.length < count) {
    if (!used.has(n)) names.push(n === 1 ? slot1 : `${job}_QA_${n}`)
    n += 1
  }
  return names
}

export function findDuplicateQaInspection(
  inspections: QaInspectionSummary[],
  sourceHash: string,
): QaInspectionSummary | undefined {
  return inspections.find((inspection) => inspection.source_hash === sourceHash)
}

export function provenanceFromPreview(plan: QaPreviewPlan): QaGenerationProvenance {
  return {
    source_hash: plan.source_hash,
    idempotency_key: plan.idempotency_key,
    source_quote_sheet_ids: plan.sources.map((source) => source.quote_sheet_id),
  }
}

export function isQaSourceReady(status: string | null | undefined): boolean {
  return status === 'approved' || status === 'accepted'
}

export function isQaSourceArchived(status: string | null | undefined): boolean {
  return status === 'archived'
}

// Reused from: packages/job-hub/src/jobs/qa/qaApiHelpers.ts (isQaSourceSelectable)
export function isQaSourceSelectable(status: string | null | undefined): boolean {
  void status
  return true
}

export function qaPlanSelectionStorageKey(jobId: number | string): string {
  return `job-hub:qa-plan-selection:${jobId}`
}

export function groupEvidenceByItem(evidence: QaEvidence[]): Record<string, QaEvidence[]> {
  const grouped: Record<string, QaEvidence[]> = {}
  for (const item of evidence) {
    if (!item.item_instance) continue
    ;(grouped[item.item_instance] ??= []).push(item)
  }
  return grouped
}

/** Hold rows stay visible. The flag is a hold point, not a reason to hide the check. */
export function isParked(_item: QaItemInstance): boolean {
  return false
}

function countDone(items: readonly QaItemInstance[]): number {
  return items.filter((item) =>
    item.status === 'done' || item.status === 'pass' || item.status === 'not_applicable',
  ).length
}

export function areaProgress(area: Pick<QaAreaExecution, 'checklists'>): {
  total: number
  done: number
  percent: number
} {
  const items = areaItems(area)
  const total = items.length
  return { total, done: countDone(items), percent: total ? Math.round((countDone(items) / total) * 100) : 0 }
}

/**
 * Every item in the area, in checklist order.
 *
 * The one place the third level is collapsed. Ordering, filtering and reorder
 * indices are area-wide, so they need this; anything that DISPLAYS rows wants
 * ``checklistGroups`` instead, or the checklist a row belongs to is lost.
 */
export function areaItems(area: Pick<QaAreaExecution, 'checklists'>): QaItemInstance[] {
  return area.checklists.flatMap((checklist) => checklist.items)
}

export interface QaChecklistGroup {
  checklist: QaChecklistInstance
  /** Display name; a checklist with neither a section nor a template has none. */
  name: string
  items: QaItemInstance[]
  total: number
  done: number
}

/**
 * The area's checklists with their progress, optionally narrowed to the items
 * currently on screen. A checklist filtered down to nothing is dropped rather
 * than shown empty, so a status filter reads the same as it did when the tab
 * rendered one flat list.
 */
export function checklistGroups(
  area: Pick<QaAreaExecution, 'checklists'>,
  visible?: readonly QaItemInstance[],
): QaChecklistGroup[] {
  const shown = visible ? new Set(visible.map((item) => item.id)) : null
  const groups: QaChecklistGroup[] = []
  for (const checklist of area.checklists) {
    const items = shown ? checklist.items.filter((item) => shown.has(item.id)) : checklist.items
    if (shown && items.length === 0) continue
    groups.push({
      checklist,
      name: checklist.template_name?.trim() || 'Checklist',
      items,
      total: checklist.items.length,
      done: countDone(checklist.items),
    })
  }
  return groups
}

export type QaOriginSection = {
  key: 'standard' | 'quote' | 'all'
  title: string
  origin: 'standard' | 'quote'
  items: QaItemInstance[]
}

/** Split one checklist into Standard checks vs From quote. Empty side is hidden. */
export function originSections(
  items: readonly QaItemInstance[],
  opts?: { flat?: boolean },
): QaOriginSection[] {
  if (opts?.flat) {
    return [{ key: 'all', title: '', origin: 'quote', items: [...items] }]
  }
  const standard = items.filter((item) => item.origin === 'standard')
  const rest = items.filter((item) => item.origin !== 'standard')
  const hasQuoteRows = rest.some((item) => item.origin === 'quote')
  if (standard.length === 0 && !hasQuoteRows) {
    return [{ key: 'all', title: '', origin: 'quote', items: [...items] }]
  }
  const sections: QaOriginSection[] = []
  if (standard.length > 0 || rest.length === 0) {
    sections.push({ key: 'standard', title: 'Standard checks', origin: 'standard', items: standard })
  }
  if (rest.length > 0 || standard.length === 0) {
    sections.push({
      key: 'quote',
      title: hasQuoteRows ? 'From quote' : 'Other checks',
      origin: 'quote',
      items: rest,
    })
  }
  return sections
}

export function formatEvidenceStamp(author?: string | null, at?: string | null): string {
  const when = at ? new Date(at) : null
  const time = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : ''
  return [author?.trim(), time].filter(Boolean).join(' · ')
}
